<script setup lang="ts">
import Paginator from 'primevue/paginator';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { useGlobalAssignmentStore } from '../../stores/useGlobalAssignmentStore';
import { useGlobalTaskAssignmentReview } from '../../composables/useGlobalTaskAssignmentReview';
import { useAssignmentReviewFiltering } from '../../composables/useAssignmentReviewFiltering';
import type { ExclusionReasonCode } from '@/features/maintenance/types';
import type { AssignmentFilterSnapshot } from '../../types/assignmentFilters';
import type { RecommendedMaintenanceTaskDetailDto } from '@/shared/services/maintenanceAssignments';
import { addToast } from '@/shared/utils/toastMessages';
import { formatApiError } from '@/shared/utils/errorFormatters';
import AssignmentAiBasketControls from '../../components/assignment-ai/AssignmentAiBasketControls.vue';
import { useAssignmentAiAvailability } from '../../composables/useAssignmentAiAvailability';
import { useAssignmentAiScopeStore } from '../../stores/useAssignmentAiScopeStore';

definePage({ meta: { title: 'Assignment Review' } });

const route = useRoute();
const router = useRouter();
const store = useGlobalAssignmentStore();
const toast = useToast();

// The sidebar menu links here directly with no "from" marker; only the per-analysis review page
// sets it when linking here, so the back button only shows for that in-app navigation.
const showBackButton = computed(() => route.query.from === 'analysis');

const {
  isLoading,
  error: storeError,
  totalCount,
  coveredCount,
  uncoveredCount,
  excludedCount,
  runToFailureCount,
  matchedCount,
  pageSize,
  lastLoadStatus,
} = storeToRefs(store);

// Selection is lifted to the page so the review composable (right-pane eligibility) and the
// filtering composable (Apply-time auto-deselect) share one Set.
const selectedRecommendationIds = ref(new Set<string>());

// The applied saved-filter snapshot lives inside the filtering composable, which is constructed
// after the review composable (it needs review.rows / review.eligibleTaskLists). This holder ref
// bridges the two without a temporal-dead-zone issue: filtering writes it via the watch below,
// and the review composable reads it live in buildQuery.
const pinHolder = shallowRef<AssignmentFilterSnapshot | null>(null);

const {
  rows,
  filters,
  methodFilter,
  eligibleTaskLists,
  activeFilter,
  selectionCount,
  selectionHasIneligibleCoverage,
  allFilteredSelected,
  isMutating,
  showExcludeDialog,
  excludeDialogTargetIds,
  load,
  toggleSelectRecommendation,
  selectAll,
  clearSelection,
  openExcludeDialog,
  closeExcludeDialog,
  handleExcludeConfirm,
  handleAssignToJobPlan,
  handleUnassign,
  handleRestore,
} = useGlobalTaskAssignmentReview({
  selectedRecommendationIds,
  appliedFilterSnapshot: pinHolder,
});

const {
  filters: filterList,
  selectedFilterId,
  appliedFilterSnapshot,
  isStale,
  healthByFilterId,
  visibleCandidateLists,
  isMutating: filtersMutating,
  error: filtersError,
  selectionDropNotice,
  loadFilters,
  apply: applyFilter,
  clear: clearFilter,
  showFilterDialog,
  dialogFilter,
  openCreateDialog,
  openEditDialog,
  onSaveFilter,
  confirmDeleteFilter,
  onEditorDelete,
  clearSelectionDropNotice,
} = useAssignmentReviewFiltering({
  // Server pages pass the current page's recommendations so Apply's auto-deselect is safe
  // against leftover rows; the left rule itself already ran server-side before paging.
  recommendations: computed(() =>
    rows.value
      .map(row => row.recommendation)
      .filter((rec): rec is RecommendedMaintenanceTaskDetailDto => Boolean(rec?.id)),
  ),
  eligibleCandidateLists: computed(() => eligibleTaskLists.value.lists),
  selectedRecommendationIds,
  leftExecution: { mode: 'server', reload: (page) => load(page) },
});

// Mirror the filtering composable's snapshot into the review composable's pin so buildQuery
// carries filterId + filterRowVersion on every page request. Immediate so a restored snapshot
// pins even the first load, and flush: 'sync' because filtering.apply/clear reload the page in
// the same synchronous turn — a deferred watcher would send the first query with the stale pin.
watch(() => appliedFilterSnapshot.value, (value) => {
  pinHolder.value = value
}, { immediate: true, flush: 'sync' })

// Coverage tabs re-query the server rather than filtering the loaded page: this is a window onto
// every analysis, so client-side filtering would silently hide out-of-page rows.
watch(activeFilter, () => void load(1));

// Pin-related load failures branch here (the store keeps the last page on 409):
// - 409: the pinned filter was edited concurrently — refresh the list so isStale turns on the
//   "Changed, Apply again" badge; keep the snapshot so re-applying pins the new version.
// - 404: the filter no longer exists — drop the snapshot and reload page 1 without a pin.
// - 422: the server refuses the definition — drop the snapshot, toast the reason, reload.
watch(lastLoadStatus, async (status) => {
  if (status === 409) {
    await loadFilters();
    return;
  }
  if (status === 404) {
    try {
      await loadFilters();
      await clearFilter();
    } catch (err) {
      addToast(toast, {
        severity: 'error',
        summary: 'Failed to clear filter',
        detail: formatApiError(err),
      });
    }
    return;
  }
  if (status === 422) {
    const detail = storeError.value ?? 'The filter was rejected by the server.';
    try {
      await loadFilters();
      await clearFilter();
    } catch (err) {
      addToast(toast, {
        severity: 'error',
        summary: 'Failed to clear filter',
        detail: formatApiError(err),
      });
      return;
    }
    addToast(toast, { severity: 'error', summary: 'Filter rejected', detail });
  }
});

onMounted(() => {
  void Promise.all([load(1), loadFilters()]);
});

// AI scope basket (additive): read-only views over the page's own selection and rows.
const { isAvailable: aiAvailable } = useAssignmentAiAvailability();
const aiScopeStore = useAssignmentAiScopeStore();
const selectedRows = computed(() =>
  rows.value.filter(row => selectedRecommendationIds.value.has(row.recommendation?.id ?? '')),
);
const noAiScopeIds: ReadonlySet<string> = new Set<string>();
const aiScopeIds = computed<ReadonlySet<string>>(() =>
  aiAvailable.value ? new Set(aiScopeStore.recommendations.keys()) : noAiScopeIds,
);

const first = computed(() => ((store.currentPage ?? 1) - 1) * (pageSize.value ?? 50));

async function onPage(event: { page: number }): Promise<void> {
  await load(event.page + 1);
}

async function onExcludeConfirm(payload: { reasonCode: ExclusionReasonCode; reason: string }): Promise<void> {
  await handleExcludeConfirm(payload.reasonCode, payload.reason);
}

async function onOpenTaskList(taskListId: string): Promise<void> {
  if (!taskListId) return;
  await router.push({
    path: '/maintenance/planning',
    query: { tab: 'taskLists', taskListId },
  });
}

async function onOpenAnalysis(analysisId: string): Promise<void> {
  if (!analysisId) return;
  await router.push({
    path: '/maintenance/analysis/edit',
    query: { analysisId },
  });
}
</script>

<template>
  <Page>
    <div class="flex h-full flex-col gap-2">
      <h1 class="text-base font-semibold text-text lg:sr-only">Assignment Review</h1>

      <Message v-if="storeError" severity="error" :closable="false" class="shrink-0">{{ storeError }}</Message>

      <!-- Counts describe the whole filtered scope, not this page or the coverage tab. -->
      <AssignmentReadinessHeader
        :uncovered-count="uncoveredCount"
        :covered-count="coveredCount"
        :excluded-count="excludedCount"
        :total-count="totalCount"
        :run-to-failure-count="runToFailureCount"
      />

      <!-- Optional navigation layer: explicit saved filters, applied only on Apply. The live
           scope (#scope) sits inside the same toolbar card. -->
      <AssignmentReviewToolbar
        embedded
        :filters="filterList"
        :selected-filter-id="selectedFilterId"
        :applied-filter="appliedFilterSnapshot"
        :stale="isStale"
        :health-by-filter-id="healthByFilterId"
        :is-mutating="filtersMutating"
        :error="filtersError"
        :selection-drop-notice="selectionDropNotice"
        @update:selected-filter-id="selectedFilterId = $event"
        @apply="applyFilter()"
        @clear="clearFilter()"
        @manage="openCreateDialog"
        @edit="openEditDialog"
        @delete="confirmDeleteFilter"
        @retry="loadFilters()"
        @dismiss-drop-notice="clearSelectionDropNotice()"
      >
        <template #scope>
          <Button
            v-if="showBackButton"
            icon="fa-solid fa-arrow-left"
            severity="secondary"
            text
            rounded
            aria-label="Back"
            v-tooltip.bottom="'Back'"
            @click="router.back()"
          />
          <GlobalAssignmentScopeFilters
            embedded
            v-model="filters"
            :is-loading="isLoading"
            @apply="load(1)"
          />
          <AssignmentAiBasketControls :selected-rows="selectedRows" />
          <div class="hidden lg:flex items-center gap-1.5 text-[11px] text-secondary shrink-0 ml-auto">
            <i class="fa-solid fa-shield-halved text-primary/70"></i>
            <span>Task lists limited by eligibility rules</span>
          </div>
        </template>
      </AssignmentReviewToolbar>

      <div v-if="isLoading" class="flex flex-col gap-2 shrink-0">
        <Skeleton height="360px" border-radius="8px" />
      </div>

      <template v-else>
        <!-- Split pane — card islands -->
        <div class="flex min-h-0 min-w-0 flex-1 gap-2">
          <!-- min-w-0 overrides the flex-item default (min-width: auto), which otherwise refuses to
               shrink below the wide recommendations table's content width and pushes this whole row
               past the viewport. With it in place, the table's own overflow-auto wrapper scrolls
               horizontally instead of the page silently clipping content. -->
          <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-1.5">
            <RecommendationsPane
              :recommendations="rows"
              :selected-ids="selectedRecommendationIds"
              :active-filter="activeFilter"
              :selection-count="selectionCount"
              :all-selected="allFilteredSelected"
              :is-mutating="isMutating"
              :show-source="true"
              :show-global-fields="true"
              :show-method-filter="true"
              :method-filter="methodFilter"
              :ai-scope-ids="aiScopeIds"
              @update:active-filter="activeFilter = $event"
              @update:method-filter="methodFilter = $event"
              @toggle-select="toggleSelectRecommendation"
              @select-all="selectAll"
              @clear-selection="clearSelection"
              @open-exclude="openExcludeDialog"
              @unassign="handleUnassign"
              @restore="handleRestore"
              @open-task-list="onOpenTaskList"
              @open-analysis="onOpenAnalysis"
            />

            <Paginator
              :first="first"
              :rows="pageSize"
              :total-records="matchedCount"
              @page="onPage"
            />
          </div>

          <!-- Task list titles/tags are short, so a fixed width (rather than competing for flex
               space against the wide recommendations table) keeps this pane compact. -->
          <div class="flex min-h-0 w-80 shrink-0 flex-col">
            <JobPlansPane
              :job-plans="visibleCandidateLists"
              :selection-count="selectionCount"
              :is-mutating="isMutating"
              :selection-ineligible="selectionHasIneligibleCoverage"
              :notice="eligibleTaskLists.notice"
              @assign="handleAssignToJobPlan"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Filter editor (create / edit / delete) -->
    <FilterEditorDialog
      v-model:visible="showFilterDialog"
      :filter="dialogFilter"
      :existing-filters="filterList"
      :is-mutating="filtersMutating"
      :error="filtersError"
      @save="onSaveFilter"
      @delete="onEditorDelete"
    />

    <ExcludeReasonDialog
      v-model:visible="showExcludeDialog"
      :count="excludeDialogTargetIds.length"
      @confirm="onExcludeConfirm"
      @update:visible="!$event && closeExcludeDialog()"
    />
  </Page>
</template>
